import {
  Injector,
  computed,
  inject,
  linkedSignal,
  runInInjectionContext,
  untracked,
} from '@angular/core'
import { rxResource, toSignal } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'

import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals'

import {
  EMPTY,
  Observable,
  combineLatest,
  distinctUntilChanged,
  of,
  switchMap,
  throwError,
} from 'rxjs'
import { catchError, finalize, map, tap } from 'rxjs/operators'

import { UserFormValue } from '@flowx/designer/modules/access-management/access-management.utils'
import { UserInvite } from '@flowx/designer/modules/core/dictionary/access-management.dictionary'
import {
  flattenAndSortUserWorkspaces,
  userMapToAssetSummary,
} from '@flowx/designer/modules/workspaces/shared/transform-utils'
import {
  AccessManagementAssetSummary,
  AccessManagementUser,
  AccessManagementUserSummary,
  BulkInviteUsersResponse,
} from '@flowx/designer/modules/workspaces/shared/workspace.utils'
import { OrganizationAdminUsersApiService } from '@flowx/designer/shared/api/organization-admin-users.api'
import { UsersType } from '@flowx/designer/shared/entities/utils'
import {
  selectAllRouteParams,
  selectAllSegments,
  selectedUserId,
} from '@flowx/designer/shared/store/selectors/router.selectors'

import { UsersGridSearch } from './users-grid-search'

import { OrgAdminUsersDeps } from '../access-management-store.types'

const errorTitle = (error): string => (error.error?.title ? error.error.title : 'Error message')

/**
 * Users feature for the org-admin scope. Designer and runtime are sibling routes sharing this
 * store, so both concrete APIs are injected and a small `usersApi(type)` picker selects between
 * them — no token, no resolver, no `as` casts.
 *
 * Type-agnostic detail operations (getUser, workspaces, admin-role) use the designer instance,
 * matching the previous "org token defaults to designer" behavior; list/grid and per-entity
 * operations route by the user type passed in or derived from the current route.
 */
// The return type of signalStoreFeature is an inferred generic that must not be widened.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function withOrgAdminUsersFeature() {
  return signalStoreFeature(
    { props: type<OrgAdminUsersDeps>() },
    withState({ usersLoading: false, userLoading: false }),
    withProps((store) => ({
      /** Picks the concrete API for a user type; defaults to designer. */
      usersApi: (usersType: UsersType): OrganizationAdminUsersApiService => {
        switch (usersType) {
          case UsersType.RUNTIME:
            return store._runtimeUsersApi

          case UsersType.SELF:
            return store._selfEditUserApi

          default:
            return store._designerUsersApi
        }
      },
      selectedUserId: store._ngrxStore.selectSignal(selectedUserId),
      /** Runtime routes carry a `runtime` URL segment; designer is the default. */
      currentUserType: toSignal(
        combineLatest([
          store._ngrxStore.select(selectAllSegments),
          store._ngrxStore.select(selectAllRouteParams),
        ]).pipe(
          map(([segments]) =>
            segments.some((segment) => segment.path === 'runtime')
              ? UsersType.RUNTIME
              : UsersType.DESIGNER
          ),
          distinctUntilChanged()
        ),
        { initialValue: UsersType.DESIGNER }
      ),
    })),
    withProps((store) => {
      const injector = inject(Injector)
      const router = inject(Router)

      return {
        /** Re-created whenever the route's user type changes, so the grid never shows stale rows. */
        usersGridSearch: linkedSignal<UsersType, UsersGridSearch>({
          source: store.currentUserType,
          computation: (userType, previous) => {
            previous?.value?.manualDestroy()

            return untracked(() =>
              runInInjectionContext(
                injector,
                () =>
                  new UsersGridSearch(
                    store.usersApi(userType),
                    router.routerState.snapshot.root.queryParams?.searchQuery
                  )
              )
            )
          },
        }),
      }
    }),
    withProps((store) => ({
      selectedUserResource: rxResource({
        params: () => ({ selectedUserId: store.selectedUserId() }),
        stream: ({ params: { selectedUserId } }) => {
          if (!selectedUserId) {
            return of(null)
          }

          patchState(store, { usersLoading: true })

          // Org-admin user detail is designer-scoped (runtime users have their own module).
          return store._designerUsersApi.getUser(selectedUserId).pipe(
            finalize(() => patchState(store, { usersLoading: false })),
            catchError((error) => {
              store._toastMessageService.showErrorMessage(
                `Failed to get user: ${errorTitle(error)}`
              )
              return EMPTY
            })
          )
        },
      }),
      userWorkspacesResource: rxResource({
        params: () => ({ selectedUserId: store.selectedUserId() }),
        stream: ({ params: { selectedUserId } }) => {
          if (!selectedUserId) {
            return of([])
          }

          patchState(store, { usersLoading: true })

          return store._designerUsersApi.getUserWorkspaces(selectedUserId).pipe(
            finalize(() => patchState(store, { usersLoading: false })),
            map((userWorkspaces) => flattenAndSortUserWorkspaces(userWorkspaces)),
            catchError((error) => {
              store._toastMessageService.showErrorMessage(
                `Failed to get workspaces: ${errorTitle(error)}`
              )
              return EMPTY
            })
          )
        },
      }),
      // Kept so the groups feature can update a user's group list; org-admin user detail shows
      // workspaces rather than groups, so it resolves empty.
      userGroupsResource: rxResource({
        params: () => ({ selectedUserId: store.selectedUserId() }),
        stream: () => of([]),
      }),
    })),
    withComputed((store) => ({
      selectedUser: computed(() =>
        store.selectedUserResource.hasValue() ? store.selectedUserResource.value() : null
      ),
      userWorkspaces: computed(() => store.userWorkspacesResource.value()),
      userGroups: computed(() => store.userGroupsResource.value()),
    })),
    withMethods((store) => ({
      searchUsers(filterKey: string): void {
        store.usersGridSearch().search({ filterKey })
      },
      refreshUsersGrid(): void {
        store.usersGridSearch().refresh()
      },
      getUserSummaries(): Observable<AccessManagementUserSummary[]> {
        patchState(store, { usersLoading: true })

        return store._designerUsersApi.getUserSummaries().pipe(
          finalize(() => patchState(store, { usersLoading: false })),
          catchError((error) => {
            store._toastMessageService.showErrorMessage(`Failed to get users: ${errorTitle(error)}`)
            return EMPTY
          })
        )
      },
      getUserAssetsSummary(
        userId: string,
        userType: UsersType
      ): Observable<AccessManagementAssetSummary[]> {
        patchState(store, { usersLoading: true })

        return store
          .usersApi(userType)
          .getUserAssetsSummary(userId)
          .pipe(
            switchMap((assetsWithWorkspaces) =>
              store._applicationsService
                .getApplicationSummaryByIds(assetsWithWorkspaces.assets.map((asset) => asset.id))
                .pipe(
                  map((appSummaries) => userMapToAssetSummary(assetsWithWorkspaces, appSummaries))
                )
            ),
            finalize(() => patchState(store, { usersLoading: false })),
            catchError((error) => {
              store._toastMessageService.showErrorMessage(
                `Failed to get user assets: ${errorTitle(error)}`
              )
              return EMPTY
            })
          )
      },
      getUserForEditing(userId: string, usersType: UsersType): Observable<AccessManagementUser> {
        return store.usersApi(usersType).getUserForEditing(userId)
      },
      createUser(payload: UserFormValue): Observable<AccessManagementUser> {
        patchState(store, { userLoading: true })

        return store
          .usersApi(store.currentUserType())
          .createUser(payload)
          .pipe(
            finalize(() => patchState(store, { userLoading: false })),
            catchError((error) => throwError(() => error))
          )
      },
      updateUser(payload: UserFormValue, usersType: UsersType): Observable<AccessManagementUser> {
        patchState(store, { userLoading: true })
        return store
          .usersApi(usersType)
          .updateUser(payload)
          .pipe(
            finalize(() => {
              store.usersGridSearch().refresh()
              patchState(store, { userLoading: false })
            }),
            catchError((error) => throwError(() => error))
          )
      },
      inviteUser(userInvite: UserInvite, usersType: UsersType): Observable<AccessManagementUser> {
        patchState(store, { userLoading: true })
        return store
          .usersApi(usersType)
          .inviteUser(userInvite)
          .pipe(
            finalize(() => patchState(store, { userLoading: false })),
            catchError((error) => throwError(() => error))
          )
      },
      bulkInviteUser(file: File, usersType: UsersType): Observable<BulkInviteUsersResponse> {
        const formData = new FormData()
        formData.append('file', file)

        return store.usersApi(usersType).bulkInviteUser(formData)
      },
      emailExists(email: string): Observable<boolean> {
        return store._runtimeUsersApi.emailExists(email).pipe(map(({ emailExists }) => emailExists))
      },
      updatePassword(userId: string, password: string, usersType: UsersType): Observable<void> {
        return store.usersApi(usersType).updatePassword(userId, password)
      },
      updateUserState(userId: string, enabled: boolean, usersType: UsersType): Observable<void> {
        // Route by the list context (like deleteUser), not the user's designerUser flag: a designer user
        // shown in the Runtime list must be acted on through the runtime endpoint (ORG_RUNTIME_USERS_EDIT),
        // which the organization owner holds, rather than the designer endpoint (ORG_USERS_EDIT), which it lacks.
        return store
          .usersApi(usersType)
          .updateUserState(userId, { enabled })
          .pipe(tap(() => store.usersGridSearch().refresh()))
      },
      resendInvite(email: string, userType: UsersType): Observable<void> {
        return store
          .usersApi(userType)
          .resendInvite(email)
          .pipe(
            tap(() => store.usersGridSearch().refresh()),
            catchError(() => {
              store._toastMessageService.showErrorMessage(
                `Failed to resend invite for user: ${email}`
              )
              return EMPTY
            })
          )
      },
      deleteUser(userId: string, userType: UsersType): Observable<void> {
        return store
          .usersApi(userType)
          .deleteUser(userId)
          .pipe(
            tap(() => {
              store.usersGridSearch().refresh()

              store._toastMessageService.showSuccessMessage(`User has been deleted!`)
            }),
            catchError((error) => {
              store._toastMessageService.showErrorMessage(
                `Failed to delete user: ${errorTitle(error)}`
              )
              return EMPTY
            })
          )
      },
    })),
    withHooks({
      onDestroy(store) {
        store.usersGridSearch()?.manualDestroy()
      },
    })
  )
}
